import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Loader2, User, Mail, Phone, MapPin, Star, CheckCircle2, AlertTriangle, FileText, Briefcase, Sparkles, RefreshCw, Wallet, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";

export default function ApplicationInsightsDialog({ applicationId, open, onOpenChange }) {
  const { authHeader } = useAuth();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    if (!applicationId) return;
    setLoading(!force);
    setRefreshing(force);
    try {
      const params = new URLSearchParams();
      params.set("lang", lang || "en");
      if (force) params.set("refresh", "true");
      const url = `${API}/employer/applications/${applicationId}/insights?${params.toString()}`;
      const r = await axios.get(url, { headers: authHeader });
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Failed to load insights" });
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => {
    if (open && applicationId) { setData(null); load(); }
    // eslint-disable-next-line
  }, [open, applicationId, lang]);

  const insights = data?.insights;
  const cand = data?.candidate;
  const job = data?.job;
  // Fall back to the application's own match_score (populated by the auto-scorer or
  // a prior Matcher run) when the freshly-generated insights returned 0 due to an
  // LLM parsing glitch. This fixes the "dashboard shows 57% but Insights shows 0%" bug.
  const score = (insights?.match_score && insights.match_score > 0)
    ? insights.match_score
    : (data?.application?.match_score ?? null);
  const scoreColor = score == null ? "text-slate-400" : score >= 60 ? "text-emerald-600" : score >= 40 ? "text-amber-600" : "text-rose-600";
  const cvFileId = data?.application?.cv_file_id || null;

  const downloadCv = async () => {
    if (!cvFileId) return;
    try {
      const r = await axios.get(`${API}/files/${cvFileId}/download`, { headers: authHeader, responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Try to derive filename from Content-Disposition; fall back to candidate name
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      a.download = m ? m[1] : `${cand?.name || "candidate"}_cv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Could not download CV — you may not have access or the file was removed.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="application-insights-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#1A8FE3]" />
            {lang === "fr" ? "Aperçu du candidat" : "Candidate Insights"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {lang === "fr"
              ? "Résumé, points forts, points faibles et score de match pour ce poste (par notre système)."
              : "Summary, strengths, weaknesses and match score for this job (by our system)."}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-16 text-center text-slate-500" data-testid="insights-loading">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3 text-[#1A8FE3]" />
            <p className="text-sm">{lang === "fr" ? "Analyse du CV…" : "Analyzing CV…"}</p>
          </div>
        )}

        {!loading && data && data.error && (
          <div className="py-10 text-center text-rose-600 text-sm">{data.error}</div>
        )}

        {!loading && data && !data.error && (
          <div className="space-y-5">
            {/* Header card: name + match score */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-1">
                  <User className="h-3.5 w-3.5" />
                  {lang === "fr" ? "Candidat" : "Candidate"}
                </div>
                <h3 className="font-display text-2xl font-bold text-slate-900 truncate" data-testid="insights-candidate-name">{cand?.name || "—"}</h3>
                {cand?.title && <div className="text-sm text-slate-600 mt-0.5">{cand.title}</div>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
                  {cand?.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{cand.email}</span>}
                  {cand?.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{cand.phone}</span>}
                  {cand?.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{cand.location}</span>}
                </div>
                {job?.title && (
                  <div className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-2.5 py-1">
                    <Briefcase className="h-3 w-3" />
                    {lang === "fr" ? "Postule pour" : "Applied for"}: <span className="font-medium text-slate-700 ml-0.5">{job.title}</span>
                  </div>
                )}
              </div>
              <div className="text-center shrink-0">
                <div className={`font-display text-5xl font-bold ${scoreColor}`} data-testid="insights-match-score">
                  {score != null ? score : "—"}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1 inline-flex items-center gap-1">
                  <Star className="h-3 w-3" />{lang === "fr" ? "Score de match" : "Match score"} / 100
                </div>
                <Button onClick={() => load(true)} disabled={refreshing} variant="ghost" size="sm" className="mt-2 h-7 text-[11px] text-slate-500 hover:text-[#1A8FE3]" data-testid="insights-refresh">
                  <RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
                  {lang === "fr" ? "Régénérer" : "Re-analyze"}
                </Button>
              </div>
            </div>

            {/* Candidate-provided fields (motivations + salary expectations) */}
            {(data?.application?.cover_letter || data?.application?.salary_expectation) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data?.application?.salary_expectation && (
                  <div className="p-4 rounded-xl border border-sky-200 bg-sky-50/40" data-testid="insights-salary-expectation">
                    <h4 className="text-[11px] uppercase tracking-widest text-sky-700 font-semibold mb-2 inline-flex items-center gap-1.5">
                      <Wallet className="h-3.5 w-3.5" />
                      {lang === "fr" ? "Prétentions salariales" : "Salary expectations"}
                    </h4>
                    <p className="text-sm text-slate-800 font-medium">{data.application.salary_expectation}</p>
                  </div>
                )}
                {data?.application?.cover_letter && (
                  <div className="p-4 rounded-xl border border-slate-200 bg-white" data-testid="insights-motivations">
                    <h4 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
                      <MessageSquare className="h-3.5 w-3.5" />
                      {lang === "fr" ? "Motivations" : "Motivations"}
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{data.application.cover_letter}</p>
                  </div>
                )}
              </div>
            )}

            {/* AI summary */}
            {insights?.summary && (
              <div className="p-4 rounded-xl bg-sky-50 border border-sky-100" data-testid="insights-summary">
                <h4 className="text-[11px] uppercase tracking-widest text-sky-700 font-semibold mb-2">
                  {lang === "fr" ? "Résumé" : "Summary"}
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed">{insights.summary}</p>
              </div>
            )}

            {/* Match reason */}
            {insights?.match_reason && (
              <div className="p-4 rounded-xl bg-white border border-slate-200">
                <h4 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                  {lang === "fr" ? "Pourquoi ce score" : "Why this score"}
                </h4>
                <p className="text-sm text-slate-700 italic">"{insights.match_reason}"</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Strengths */}
              {insights?.strengths?.length > 0 && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/40" data-testid="insights-strengths">
                  <h4 className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold mb-3 inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {lang === "fr" ? "Points forts" : "Strengths"}
                  </h4>
                  <ul className="space-y-2">
                    {insights.strengths.map((s, i) => (
                      <li key={i} className="flex gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weaknesses */}
              {insights?.weaknesses?.length > 0 && (
                <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/40" data-testid="insights-weaknesses">
                  <h4 className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold mb-3 inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {lang === "fr" ? "Points faibles" : "Weaknesses"}
                  </h4>
                  <ul className="space-y-2">
                    {insights.weaknesses.map((s, i) => (
                      <li key={i} className="flex gap-2 text-sm text-slate-700">
                        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
              {insights?.generated_at && (
                <span>
                  {lang === "fr" ? "Généré le" : "Generated"} {new Date(insights.generated_at).toLocaleString()}
                </span>
              )}
              {cvFileId && (
                <button type="button" onClick={downloadCv} data-testid="insights-download-cv" className="inline-flex items-center gap-1 text-[#1A8FE3] hover:underline font-medium">
                  <FileText className="h-3.5 w-3.5" />
                  {lang === "fr" ? "Télécharger le CV" : "Download CV"}
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
